/**
 * src/components/legal/ConsentGate.tsx — blocking agreement gate (web).
 *
 * Web counterpart to attic-mobile's ConsentGate, against the same endpoints
 * and the same rules. Sits inside ProtectedRoute and ABOVE the onboarding
 * latch, so it applies to every authenticated route — including one reached by
 * pasting a deep link — and can re-prompt customers who finished onboarding
 * months ago when the Terms materially change.
 *
 * Declining does NOT lock the account. It drops to read-only: crates stay
 * visible, returns and cancellation stay open, only new storage is blocked
 * (enforced server-side in resources/lib/consentGuard.js).
 *
 * Replaces the passive "by continuing you agree" text on LoginPage,
 * which is browsewrap and largely unenforceable.
 */
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router';

import {
  getConsentStatus,
  recordConsent,
  isConsentSatisfied,
  type ConsentStatus,
  type LegalDocument,
} from '../../api/consentApi';
import {
  forgetConsentCurrent,
  rememberConsentCurrent,
  wasConsentCurrent,
} from '../../api/consentCache';
import { LegalMarkdown } from './LegalMarkdown';

/** Distance from the bottom that counts as "read to the end". */
const SCROLL_EPSILON = 48;

export function ConsentGate() {
  const { userId } = useClerkAuth();
  const [status, setStatus] = useState<ConsentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Couldn't ask, but this browser remembers this customer being fully
  // consented recently. Only ever set alongside `error`.
  const [degraded, setDegraded] = useState(false);
  const [nonce, setNonce] = useState(0);

  const [index, setIndex] = useState(0);
  const [scrolled, setScrolled] = useState<Record<string, boolean>>({});
  const [ageChecked, setAgeChecked] = useState(false);
  const [pending, setPending] = useState<'accepted' | 'declined' | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const s = await getConsentStatus();
        if (cancelled) return;
        setStatus(s);
        setError(false);
        setDegraded(false);
        // Remember only a clean bill of health, and drop it the moment the
        // server says anything else — otherwise someone who declines could ride
        // a stale entry through the next outage.
        if (s.consent_state === 'current') rememberConsentCurrent(userId);
        else forgetConsentCurrent();
      } catch {
        // The live read failed. A remembered `current` is the only thing that
        // opens the gate from here; absence blocks.
        const remembered = wasConsentCurrent(userId);
        if (cancelled) return;
        setError(true);
        setDegraded(remembered);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [nonce, userId]);

  const refetch = useCallback(() => { setNonce((n) => n + 1); }, []);

  const docs = useMemo(() => status?.documents ?? [], [status]);
  const needsAge = (status?.attestations ?? []).includes('age_attestation');
  const submitting = pending !== null;

  const submit = useCallback(async (action: 'accepted' | 'declined') => {
    if (submitting) return;
    setPending(action);
    setSubmitError(null);
    try {
      await recordConsent({
        action,
        documents: docs.map((d) => ({
          doc_type: d.doc_type, uuid: d.uuid, content_hash: d.content_hash, version: d.version,
        })),
        attestations: needsAge ? ['age_attestation'] : [],
      });
      refetch();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Something went wrong.';
      if (/changed|stale|hash/i.test(msg)) {
        setSubmitError('These documents were just updated — please take another look.');
        refetch();
        setIndex(0);
      } else {
        setSubmitError(msg);
      }
    } finally {
      setPending(null);
    }
  }, [submitting, docs, needsAge, refetch]);

  const onScroll = useCallback((doc: LegalDocument) => (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_EPSILON) {
      setScrolled((s) => (s[doc.uuid] ? s : { ...s, [doc.uuid]: true }));
    }
  }, []);

  // A document too short to scroll must not strand the customer behind a
  // permanently disabled button.
  const paneRef = useCallback((doc: LegalDocument) => (el: HTMLDivElement | null) => {
    if (el && el.scrollHeight <= el.clientHeight + SCROLL_EPSILON) {
      setScrolled((s) => (s[doc.uuid] ? s : { ...s, [doc.uuid]: true }));
    }
  }, []);

  if (loading) return null;

  // Couldn't determine state ≠ consented — UNLESS this browser remembers this
  // customer being fully consented recently, in which case blocking them from
  // their own crates over a failed request is the worse error. See
  // api/consentCache.ts.
  if (error && degraded) return <Outlet />;
  if (error) {
    return (
      <div style={S.center}>
        <p style={S.errorText}>We couldn&rsquo;t load your agreements.</p>
        <button style={S.primary} onClick={refetch}>Try again</button>
      </div>
    );
  }

  if (!status) return <Outlet />;
  if (status.consent_state === 'declined') return <Outlet />; // read-only, not locked out
  if (isConsentSatisfied(status)) return <Outlet />;
  if (!docs.length) return <Outlet />; // nothing published yet

  const doc = docs[Math.min(index, docs.length - 1)];
  // docs.length was checked above, so this is unreachable — but the index
  // signature is honestly typed and letting it fall through to <Outlet/> is
  // safer than an assertion that could one day wave someone past the gate.
  if (!doc) return <Outlet />;

  const isLast = index >= docs.length - 1;
  // A returning customer whose document changed, not a new signup. The server
  // only sends documents that are actually outstanding, so anything on screen
  // is either brand new to them or genuinely changed.
  const priorVersion = doc.previously_accepted_version;
  const scrollSatisfied = !doc.requires_scroll || scrolled[doc.uuid] === true;
  const ageSatisfied = !isLast || !needsAge || ageChecked;
  const canContinue = scrollSatisfied && ageSatisfied && !submitting;

  return (
    /* Test ids, added 2026-09-04. This gate sits above every authenticated
       route, so when new Terms publish it blocks EVERY signed-in user until
       they accept — e2e fixtures included. It had no stable selectors, so no
       helper could walk it and the whole suite timed out against an invisible
       modal. A screen with legal weight should be drivable by a test. */
    <div style={S.root} data-testid="consent-gate">
      <div style={S.card}>
        <header style={S.header}>
          <p style={S.step}>
            {docs.length > 1
              ? `${index + 1} of ${docs.length}`
              : priorVersion ? 'Updated' : 'Before you start'}
          </p>
          <h1 style={S.title}>{doc.title}</h1>
          {priorVersion ? (
            <p style={S.updateNote}>
              We&rsquo;ve updated our {doc.title} since you agreed to version {priorVersion}.
              Please take a look and agree again.
            </p>
          ) : null}
          <p style={S.version}>Version {doc.version} · effective {doc.effective_at.slice(0, 10)}</p>
        </header>

        <div style={S.pane} data-testid="consent-scroll-pane" onScroll={onScroll(doc)} ref={paneRef(doc)}>
          {doc.change_summary ? (
            <div style={S.changeBox}>
              <p style={S.changeLabel}>What changed</p>
              <p style={S.changeBody}>{doc.change_summary}</p>
            </div>
          ) : null}
          <LegalMarkdown source={doc.body_markdown} />
        </div>

        <footer style={S.footer}>
          {doc.requires_scroll && !scrollSatisfied ? (
            <p style={S.hint}>Scroll to the end to continue</p>
          ) : null}

          {isLast && needsAge ? (
            <label style={S.checkRow}>
              <input type="checkbox" data-testid="consent-age-check" checked={ageChecked} onChange={(e) => setAgeChecked(e.target.checked)} />
              <span style={S.checkLabel}>
                {status.attestation_copy?.age_attestation ?? 'I am 18 years of age or older'}
              </span>
            </label>
          ) : null}

          {submitError ? <p style={S.errorText}>{submitError}</p> : null}

          <button
            data-testid="consent-accept"
            style={{ ...S.primary, ...(canContinue ? {} : S.primaryDisabled) }}
            disabled={!canContinue}
            onClick={() => { if (!isLast) { setIndex((i) => i + 1); return; } void submit('accepted'); }}
          >
            {pending === 'accepted' ? 'Saving…' : isLast ? doc.assent_text : 'Continue'}
          </button>

          <button data-testid="consent-decline" style={S.secondary} disabled={submitting} onClick={() => { void submit('declined'); }}>
            I don&rsquo;t agree
          </button>

          <p style={S.declineNote}>
            You can still get your crates back, cancel, or contact support if you don&rsquo;t agree.
          </p>
        </footer>
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh', background: 'var(--color-bg, var(--bg))', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  card: { width: '100%', maxWidth: 720, background: 'var(--color-surface, var(--surface))', border: '1px solid var(--color-border, var(--border))', borderRadius: 12, display: 'flex', flexDirection: 'column', maxHeight: '92vh', overflow: 'hidden' },
  header: { padding: '24px 24px 12px' },
  step: { margin: 0, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--color-text-muted, var(--attic-ink-3))' },
  title: { margin: '6px 0 4px', fontSize: 26, lineHeight: 1.2, color: 'var(--color-text-primary, var(--attic-ink))' },
  updateNote: { margin: '0 0 8px', fontSize: 14, lineHeight: 1.45, color: 'var(--color-text-primary, var(--attic-ink))' },
  version: { margin: 0, fontSize: 12, color: 'var(--color-text-muted, var(--attic-ink-3))' },
  pane: { flex: 1, overflowY: 'auto', padding: 24, borderTop: '1px solid var(--color-border, var(--border))', borderBottom: '1px solid var(--color-border, var(--border))' },
  changeBox: { background: 'var(--attic-mustard-soft)', borderLeft: '3px solid var(--attic-mustard)', padding: 14, marginBottom: 18 },
  changeLabel: { margin: '0 0 4px', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600, color: 'var(--attic-mustard-deep)' },
  changeBody: { margin: 0, fontSize: 14, lineHeight: 1.5 },
  footer: { padding: 20, display: 'flex', flexDirection: 'column', gap: 10 },
  hint: { margin: 0, fontSize: 13, textAlign: 'center', color: 'var(--color-text-muted, var(--attic-ink-3))' },
  checkRow: { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.4, cursor: 'pointer' },
  checkLabel: { flex: 1 },
  primary: { background: 'var(--attic-terracotta-strong)', color: '#fff', border: 'none', borderRadius: 8, padding: '15px 20px', fontSize: 16, fontWeight: 600, cursor: 'pointer' },
  primaryDisabled: { background: 'var(--attic-ink-4)', cursor: 'not-allowed' },
  secondary: { background: 'none', border: 'none', color: 'var(--color-text-muted, var(--attic-ink-3))', textDecoration: 'underline', fontSize: 15, cursor: 'pointer', padding: 8 },
  declineNote: { margin: 0, fontSize: 12, lineHeight: 1.45, textAlign: 'center', color: 'var(--color-text-muted, var(--attic-ink-3))' },
  center: { minHeight: '60vh', display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', justifyContent: 'center' },
  errorText: { margin: 0, fontSize: 14, color: 'var(--attic-berry-deep)', textAlign: 'center' },
};
